import { useEffect, useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { cn } from '../utils/cn';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  footer?: ReactNode;
  width?: number | string;
  children: ReactNode;
}

/**
 * Centered overlay dialog used for sign-in / sign-up / forgot-password
 * / confirmations / etc. across the storefront and panel.
 *
 * A11y / UX baseline:
 * - `role="dialog"` + `aria-modal="true"` so screen readers announce
 *   it as a modal context.
 * - Escape closes the modal — keyboard users expect this from native
 *   `<dialog>` semantics. The handler is registered only while
 *   `open=true` so it doesn't accumulate listeners across mounts.
 * - Body scroll is locked while the modal is up; without this, the
 *   page behind the backdrop scrolls under touch / wheel input which
 *   feels broken (especially on mobile).
 * - Backdrop click closes (existing behaviour preserved).
 */
export function Modal({ open, onClose, title, footer, width = 520, children }: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  // Capture the element that had focus before the modal opened so we
  // can restore focus there on close — without this, screen readers
  // and keyboard users lose their place when the modal dismisses.
  const previousActiveRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  // Focus management — on open: stash whatever had focus, then move
  // focus to the first focusable element inside the panel (typically
  // the first input). On close: return focus to the original element
  // so keyboard users land back on the button they clicked. The
  // first-focus pass is deferred one tick so the panel's children
  // have mounted and any `autoFocus` on a child input wins.
  useEffect(() => {
    if (!open) return;
    previousActiveRef.current =
      typeof document !== 'undefined' ? (document.activeElement as HTMLElement | null) : null;
    const t = window.setTimeout(() => {
      const panel = panelRef.current;
      if (!panel) return;
      // If a child marked itself with `autoFocus`, the browser has
      // already moved focus there — don't override it.
      if (panel.contains(document.activeElement)) return;
      // Prefer form fields over the Close-X / footer buttons. A
      // sign-in modal should land on the username input, not on the
      // X icon in the header.
      const formField = panel.querySelector<HTMLElement>(
        'input:not([disabled]):not([type="hidden"]), textarea:not([disabled]), select:not([disabled])',
      );
      if (formField) {
        formField.focus();
        return;
      }
      // Confirmation-only modals (no form fields) → focus the first
      // button so Enter triggers the primary action.
      const button = panel.querySelector<HTMLElement>(
        'button:not([disabled]):not([aria-label="Close"]), [href], [tabindex]:not([tabindex="-1"])',
      );
      button?.focus();
    }, 0);
    return () => {
      window.clearTimeout(t);
      // Restore focus to whatever had it before the modal opened.
      // Guarded against detached / removed nodes.
      const prev = previousActiveRef.current;
      if (prev && typeof prev.focus === 'function' && document.contains(prev)) {
        prev.focus();
      }
    };
  }, [open]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={typeof title === 'string' ? title : 'Dialog'}
    >
      <div
        ref={panelRef}
        style={{ width }}
        className={cn(
          'w-full max-w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] shadow-xl',
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-[var(--color-border)] px-4 py-3">
          <div className="text-sm font-semibold text-[var(--color-text-primary)]">{title}</div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-[var(--color-bg-card-hover)]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-4">{children}</div>
        {footer && (
          <div className="flex justify-end gap-2 border-t border-[var(--color-border)] px-4 py-3">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

export default Modal;
